/**
 * Page Setup Context and Provider
 *
 * This module provides React Context for managing Page Setup state and Stepper walkthrough within Auriga.
 * Create Page final save and persistence will be handled here as well.
 */

import { DEFAULT_PAGE_DRIVERS } from '@constants/page';
import { useDeckContext } from '@context/DeckContext';
import { useTOCContext } from '@context/TOCContext';
import { useUserContext } from '@context/UserContext';
import { DatasetSource, DenominatorLabelValues } from '@services/api/deckManagement/graphql/schema/API';
import { useSaveDeck, UseSaveDeckParams } from '@services/api/deckManagement/hooks';
import { useGetDecklines, useGetPagesByPageName } from '@services/api/deckManagement/hooks/usePage';
import { entityKeyAsId } from '@utils/deckHelpers';
import { datasetSourceFromDrivers, readPageDriverSets, writePageDriverSets } from '@utils/kpiDriverHelpers';
import { isEmpty, isNil } from 'lodash';
import { createContext, ReactNode, use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ChangeSetTargets, SaveDeckChangeSetSchema } from 'src/schema';
import { LockStatus } from 'types/DeckContext.types';
import { PageGranularity } from 'types/Page.types';
import { PageSetupContextType, PageSetupProviderProps, PageSetupSteps } from 'types/PageSetupContext.types';
import { AnyKPIDrivers } from 'types/PageSidePanel.types';

/**
 * Default ordering of Page Setup steps
 */
const PAGE_SETUP_STEP_ORDER: PageSetupSteps[] = [
  PageSetupSteps.PAGE_DETAILS,
  PageSetupSteps.TABLE_CONFIGURATION,
  PageSetupSteps.PAGE_DRIVER,
];

export const PageSetupContext = createContext<PageSetupContextType>({
  pageSetupSteps: Object.values(PageSetupSteps),
  currentStep: PageSetupSteps.PAGE_DETAILS,
  nextStep: PageSetupSteps.TABLE_CONFIGURATION,
  setCurrentStep: () => {},
  currDeckId: null,
  currPageName: null,
  tocId: null,
  setTocId: () => {},
  tocEntry: undefined,
  isPageCreated: false,
  setIsPageCreated: () => {},
  pageName: '',
  setPageName: () => {},
  tocName: '',
  setTocName: () => {},
  pageHeaderName: '',
  setPageHeaderName: () => {},
  reportingPeriods: [],
  setReportingPeriods: () => {},
  denominatorLabel: null,
  setDenominatorLabel: () => {},
  selectedLayoutFamily: '',
  setSelectedLayoutFamily: () => {},
  assignLayoutFamily: () => {},
  isLayoutFamilySaving: false,
  hasTableConfigChanged: false,
  snapshotTableConfig: () => {},
  resetTableConfig: () => {},
  isUpdateTemplate: false,
  setIsUpdateTemplate: () => {},
  availableGrains: [],
  pageDriverSets: { [DatasetSource.GALAXY]: DEFAULT_PAGE_DRIVERS },
  setPageDriverSets: () => {},
  enabledDatasets: [DatasetSource.GALAXY],
  decklineDatasetCounts: {},
  savePageDrivers: () => {},
  pageDriversSaving: false,
  pageError: null,
  pageLockStatus: { isLocked: false },
  isPageLockStatusResolved: false,
});

export const PageSetupContextProvider = ({ children }: PageSetupProviderProps): ReactNode => {
  const { username } = useUserContext();
  const { currentDeckId, currentDeck, deckLockStatus } = useDeckContext();
  const { createNewPage, getTocItemsByGroup, hasInitialDataLoaded, updateTocEntry } = useTOCContext();
  const saveDeckMutation = useSaveDeck();
  const saveDeckMutationRef = useRef(saveDeckMutation);
  saveDeckMutationRef.current = saveDeckMutation;

  // All TOC entries across groups (unfiltered)
  const allTocEntries = useMemo(() => Array.from(getTocItemsByGroup().values()).flat(), [getTocItemsByGroup]);
  // Get pageName from route parameters, if exists (will not exist from Page Library view)
  const { pageName: routePageName } = useParams<{ pageName: string }>();
  const currPageName = decodeURIComponent(routePageName || '');

  // TODO: will need to be dynamically controlled once we support the Page Denominator conditional step.
  const pageSetupSteps = useMemo(() => PAGE_SETUP_STEP_ORDER, []);

  // State for Page Setup Wizard handling
  const [currentStep, setCurrentStep] = useState<PageSetupSteps>(PAGE_SETUP_STEP_ORDER[0]);
  const nextStep = useMemo(() => {
    const currStepIdx = pageSetupSteps.indexOf(currentStep);
    return pageSetupSteps[currStepIdx + 1] ?? null;
  }, [currentStep, pageSetupSteps]);

  // Page Details Step state
  const [tocId, setTocId] = useState<string | null>(null);
  // Resolved TOC entry matching the current tocId
  const tocEntry = useMemo(() => allTocEntries.find((entry) => entry.toc_id === tocId), [allTocEntries, tocId]);
  const [isPageCreated, setIsPageCreated] = useState(false);
  const [pageName, setPageName] = useState('');
  const [tocName, setTocName] = useState('');
  const [pageHeaderName, setPageHeaderName] = useState('');
  const [reportingPeriods, setReportingPeriods] = useState<PageGranularity[]>([]);
  const [denominatorLabel, setDenominatorLabel] = useState<DenominatorLabelValues | null>(null);

  // Table Configuration Step state — reusable PageLayout family selection.
  const [selectedLayoutFamily, setSelectedLayoutFamily] = useState('');
  const [isLayoutFamilySaving, setIsLayoutFamilySaving] = useState(false);
  // Baseline of the persisted layout family, for change detection.
  const initialLayoutFamilyRef = useRef<string>('');
  // One-shot guard: seed selectedLayoutFamily + its baseline from the persisted page exactly
  // once per edit session. Without this, a pagesByName refetch (triggered by any SaveDeck's
  // global query invalidation) would re-run the seed and clobber an unsaved user selection.
  const hasSeededLayoutFamily = useRef(false);
  // True after first save, or when prepopulated from an existing page (Edit flow).
  const [isUpdateTemplate, setIsUpdateTemplate] = useState(!isEmpty(currPageName));

  // Page Drivers Step state
  const [pageId, setPageId] = useState<string | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const [pageDriversSaving, setPageDriversSaving] = useState<boolean>(false);
  // All configured page-driver sets keyed by dataset — the single source of truth, treated
  // uniformly across datasets (GALAXY has no special status here beyond being the seeded default).
  const [pageDriverSets, setPageDriverSets] = useState<Partial<Record<DatasetSource, AnyKPIDrivers>>>({
    [DatasetSource.GALAXY]: DEFAULT_PAGE_DRIVERS,
  });
  /** Datasets the deck permits a page to configure (from Deck.enabled_datasets, which always includes GALAXY). */
  const enabledDatasets = useMemo<DatasetSource[]>(
    () => (currentDeck?.enabled_datasets ?? [DatasetSource.GALAXY]).filter((ds): ds is DatasetSource => !isNil(ds)),
    [currentDeck?.enabled_datasets],
  );

  // Fetch grain pages for the current page name and deck (shared by Table Configuration + Page Drivers)
  // Gets enabled for edit workflow when currPageName exists from route, or after page creates in new page workflow
  const {
    data: pagesByName,
    isError: isPagesByNameError,
    isFetched: isPagesByNameFetched,
  } = useGetPagesByPageName({
    page_name: pageName || currPageName,
    deck_id: currentDeckId || '',
    options: {
      enabled:
        (isPageCreated || !isEmpty(currPageName)) &&
        ((!isNil(pageName) && !isEmpty(pageName)) || !isEmpty(currPageName)) &&
        !isNil(currentDeckId) &&
        !isEmpty(currentDeckId),
    },
  });

  // Decklines on the resolved page grain, rolled up to a count per dataset. The Page Driver step uses
  // this to block removing a dataset source that decklines still depend on — it never needs the full
  // deckline list, so we only surface the counts. Enabled once a concrete pageId is known (edit flow);
  // a brand-new page has no decklines yet, so the map stays empty.
  const { data: pageDecklines } = useGetDecklines({
    pageId: pageId ?? undefined,
    options: { enabled: Boolean(pageId) },
  });
  const decklineDatasetCounts = useMemo<Partial<Record<DatasetSource, number>>>(
    () =>
      (pageDecklines ?? []).reduce<Partial<Record<DatasetSource, number>>>((acc, deckline) => {
        // Blank/calculated lines carry no deckline_drivers and bind no dataset — skip them.
        if (isNil(deckline.deckline_drivers)) return acc;
        const dataset = datasetSourceFromDrivers(deckline.deckline_drivers);
        acc[dataset] = (acc[dataset] ?? 0) + 1;
        return acc;
      }, {}),
    [pageDecklines],
  );

  /**
   * Dynamically determine available grains from pagesByName
   */
  const availableGrains = useMemo(() => {
    if (!pagesByName || pagesByName.length === 0) return ['MTD', 'QTD', 'YTD'];

    const grains = pagesByName
      .map((page) => page.page_grain)
      .filter((grain): grain is string => Boolean(grain))
      .filter((grain, index, arr) => arr.indexOf(grain) === index);

    return grains.length > 0 ? grains : ['MTD', 'QTD', 'YTD'];
  }, [pagesByName]);

  /**
   * Per-page lock state for the page being set up (all grains of a logical page share one
   * lock). Authorization for page setup comes from holding the DECK lock (the
   * "Lock Deck to set up this page" flow); a page still needing setup has no per-page lock
   * yet. The user may edit when they hold the deck lock and no OTHER user holds a
   * conflicting per-page lock. Derived from the fetched grain records + deck lock.
   */
  const pageLockStatus = useMemo<LockStatus>(() => {
    const lockedGrain = pagesByName?.find((page) => Boolean(page.locked_by));
    const lockedBy = lockedGrain?.locked_by ?? undefined;
    const isLocked = Boolean(lockedBy);
    const isLockedByCurrentUser = isLocked && lockedBy === username;
    const heldByDeckLock = Boolean(deckLockStatus.isLockedByCurrentUser);
    // Another user holding a per-page lock blocks editing even if we hold the deck lock.
    const conflictingPageLock = isLocked && !isLockedByCurrentUser;
    const canEdit = isLockedByCurrentUser || (heldByDeckLock && !conflictingPageLock);
    return {
      isLocked,
      lockedBy,
      lockedAt: lockedGrain?.locked_at ?? undefined,
      lockExpiry: lockedGrain?.lock_expiry ?? undefined,
      isLockedByCurrentUser,
      isEditingDisabled: !canEdit,
    };
  }, [pagesByName, username, deckLockStatus.isLockedByCurrentUser]);

  /**
   * Whether lock status has resolved enough to drive navigation/redirects.
   *
   * - Edit flow (has currPageName): wait for the page grain records (pagesByName) to fetch,
   *   since per-page lock is derived from them.
   * - New Page flow (empty currPageName): pagesByName never fetches (no page name yet), so
   *   authorization comes solely from the DECK lock. Resolution therefore requires the deck to
   *   be loaded AND its lock status to have been computed. Without a new-page-aware signal the
   *   redirect guard could never fire on the New Page route, letting an unauthorized direct-URL
   *   visit sit on the wizard. deckLockStatus.isEditingDisabled is undefined until DeckContext
   *   derives it from the loaded deck; gating on `!== undefined` avoids a race where the deck is
   *   present but the lock is momentarily still the pre-compute default (which would otherwise
   *   trigger a spurious redirect for an authorized user).
   */
  const isPageLockStatusResolved = isEmpty(currPageName)
    ? Boolean(currentDeck) && deckLockStatus.isEditingDisabled !== undefined
    : isPagesByNameFetched;

  // Track whether initial TOC entry creation has been triggered for New Page flow
  const hasInitialized = useRef(false);
  const hasPopulatedDefaults = useRef(false);

  // On mount for New Page flow, create a TOC entry once TOC data has loaded — but ONLY when the
  // user is authorized to edit (holds the deck lock). Authorization is required here because the
  // New Page route can be reached by direct URL (bypassing the deck-lock-gated New Page button);
  // without this check an unauthorized visit would silently auto-create a TOC entry.
  //
  // Fail-closed: require deckLockStatus.isEditingDisabled to be EXPLICITLY false. It is undefined
  // until DeckContext derives it from the loaded deck; a truthiness check (!isEditingDisabled)
  // would pass during that unresolved window and auto-create before authorization is known. The
  // effect re-runs when the deck lock resolves, so an authorized user still gets the auto-create.
  useEffect(() => {
    if (
      isEmpty(currPageName) &&
      !tocId &&
      !hasInitialized.current &&
      hasInitialDataLoaded &&
      deckLockStatus.isEditingDisabled === false
    ) {
      hasInitialized.current = true;
      createNewPage((newTocId: string) => {
        setTocId(newTocId);

        // Add an incomplete identifier to the otherwise unused group_name field, so we can filter out entries from
        // the side menu, and add information in the case of early navigation to the page view.
        // Follows the format: SETUP_INCOMPLETE|CURRENT_STEP
        updateTocEntry(newTocId, 'group_name', `SETUP_INCOMPLETE|${PageSetupSteps.PAGE_DETAILS}`);
      });
    }
  }, [currPageName, tocId, createNewPage, hasInitialDataLoaded, updateTocEntry, deckLockStatus.isEditingDisabled]);

  // Prepopulate form fields from the created TOC entry once available (New Page Flow)
  useEffect(() => {
    if (tocId && isEmpty(currPageName) && !hasPopulatedDefaults.current) {
      const createdEntry = allTocEntries.find((entry) => entry.toc_id === tocId);
      if (createdEntry) {
        hasPopulatedDefaults.current = true;
        setPageName(createdEntry.page_name || '');
        setTocName(createdEntry.toc_page_name || '');
        setPageHeaderName(createdEntry.page_name || '');
      }
    }
  }, [tocId, allTocEntries, currPageName]);

  // Prepopulate name fields from existing page/TOC data (Edit Page Flow).
  // Template/section configuration is no longer derived here; pages render from
  // their assigned layout_family via getPageLayout.
  useEffect(() => {
    if (isEmpty(currPageName) || hasPopulatedDefaults.current) return;
    if (allTocEntries.length === 0) return;

    const tocItem = allTocEntries.find((entry) => entry.page_name === currPageName);
    setPageName(currPageName);
    setTocName(tocItem?.toc_page_name || 'Unknown TOC Name');
    setPageHeaderName(tocItem?.page_name || currPageName);
    hasPopulatedDefaults.current = true;

    if (pagesByName && pagesByName.length > 0) {
      setIsPageCreated(true);
    }
  }, [currPageName, pagesByName, allTocEntries]);

  /**
   * Handles saving Page Drivers, and Page denominator_label updates back to the Deck.
   * Persists only the page's per-dataset `page_drivers` (a `KPIDriversInput` union list, one wrapper
   * per configured dataset) and `denominator_label`. Decklines are intentionally NOT written here: the
   * backend derives each deckline's drivers/kpi_id at read from the page + variant, so there is no
   * client-side page-driver -> deckline propagation.
   * @param successCallback - Optional callback function to execute on successful save
   * @param failureCallback - Optional callback function to execute on save failure
   */
  const savePageDrivers = useCallback(
    (successCallback?: () => void, failureCallback?: (error: Error) => void) => {
      if (!pageId || !currentDeckId) {
        failureCallback?.(new Error('Page data not yet loaded.'));
        return;
      }

      setPageDriversSaving(true);

      // page_drivers is a LIST of the KPIDriversInput union — one wrapper per configured dataset.
      const driverSetsPayload = writePageDriverSets(pageDriverSets);

      // Only the page's drivers + denominator_label are persisted. Decklines are NOT updated here:
      // the backend derives each deckline's drivers/kpi_id at read from the page + variant, so there
      // is no client-side page-driver -> deckline propagation to perform.
      const changeSet: Record<string, unknown> = {
        [ChangeSetTargets.page]: {
          update: [
            {
              page_id: pageId,
              page_drivers: driverSetsPayload,
              denominator_label: denominatorLabel, // Carried over from user assignment at Page Details step
            },
          ],
        },
      };

      const validatedChangeSet = SaveDeckChangeSetSchema.parse(changeSet);

      const saveDeckParams: UseSaveDeckParams = {
        deckId: currentDeckId || undefined,
        deckName: currentDeck?.deck_name || undefined,
        change_set: validatedChangeSet,
        currentUser: username,
        successCallback,
      };

      saveDeckMutationRef.current.mutate(saveDeckParams, {
        onError: (saveError) => {
          console.error('Save operation failed:', saveError);
          failureCallback?.(saveError);
        },
        onSettled: () => {
          setPageDriversSaving(false);
        },
      });
    },
    [currentDeck?.deck_name, currentDeckId, denominatorLabel, pageDriverSets, pageId, username],
  );

  /**
   * Assigns the catalog-selected layout family to every created grain Page via a
   * SaveDeck page.update. Must run only after the grain Pages exist (post TOC/page
   * creation) so getPageLayout can resolve each grain's template.
   * @param successCallback - Optional callback on successful save
   * @param failureCallback - Optional callback on save failure
   */
  const assignLayoutFamily = useCallback(
    (successCallback?: () => void, failureCallback?: (error: Error) => void) => {
      if (!currentDeckId) {
        failureCallback?.(new Error('Deck data not yet loaded.'));
        return;
      }
      if (isEmpty(selectedLayoutFamily)) {
        failureCallback?.(new Error('No layout family selected.'));
        return;
      }
      if (!pagesByName || pagesByName.length === 0) {
        failureCallback?.(new Error('No created pages found to assign a layout family.'));
        return;
      }

      setIsLayoutFamilySaving(true);

      const changeSet: Record<string, unknown> = {
        [ChangeSetTargets.page]: {
          update: pagesByName.map((page) => ({
            page_id: entityKeyAsId(page.pk),
            layout_family: selectedLayoutFamily,
          })),
        },
      };

      let validatedChangeSet;
      try {
        validatedChangeSet = SaveDeckChangeSetSchema.parse(changeSet);
      } catch (validationError) {
        // Zod parse throws synchronously; without this guard the mutation (and its
        // onSettled) never runs, leaving isLayoutFamilySaving stuck true.
        console.error('Layout family changeset validation failed:', validationError);
        setIsLayoutFamilySaving(false);
        failureCallback?.(validationError instanceof Error ? validationError : new Error('Invalid change set.'));
        return;
      }

      const saveDeckParams: UseSaveDeckParams = {
        deckId: currentDeckId || undefined,
        deckName: currentDeck?.deck_name || undefined,
        change_set: validatedChangeSet,
        currentUser: username,
        successCallback: () => {
          successCallback?.();
        },
      };

      saveDeckMutationRef.current.mutate(saveDeckParams, {
        onError: (saveError) => {
          console.error('Layout family assignment failed:', saveError);
          failureCallback?.(saveError);
        },
        onSettled: () => {
          setIsLayoutFamilySaving(false);
        },
      });
    },
    [currentDeck?.deck_name, currentDeckId, pagesByName, selectedLayoutFamily, username],
  );

  // Determines if the layout template selection has changed from its initial persisted state
  const hasTableConfigChanged = useMemo(
    (): boolean => selectedLayoutFamily !== initialLayoutFamilyRef.current,
    [selectedLayoutFamily],
  );

  /** Updates the baseline snapshot to the current selection (call after a successful save) */
  const snapshotTableConfig = useCallback(() => {
    initialLayoutFamilyRef.current = selectedLayoutFamily;
  }, [selectedLayoutFamily]);

  /** Restores the layout template selection to the initial persisted state */
  const resetTableConfig = useCallback(() => {
    setSelectedLayoutFamily(initialLayoutFamilyRef.current);
  }, []);

  /**
   * Effect to handle API GetPagesByPageName fetch errors
   */
  useEffect(() => {
    if (isPagesByNameError && pageName) {
      setPageError(`Failed to fetch page data for "${pageName}". Unable to proceed.`);
      setPageDriverSets({ [DatasetSource.GALAXY]: DEFAULT_PAGE_DRIVERS });
    } else if (!isPagesByNameError && pageError) {
      // Clear error when API error state changes from error to success
      setPageError(null);
    }
  }, [isPagesByNameError, pageName, pageError]);

  /**
   * Handles setting the Page ID and Page Drivers when the page name changes
   */
  useEffect(() => {
    if (pagesByName && pagesByName.length > 0) {
      // NOTE: `page_drivers` are synchronized across all grains in the backend service.
      // Because of this, we can just use the first filtered (by Page Name) result to retrieve the drivers.
      const targetPage = pagesByName.filter((page) => page.page_name === pageName)[0];

      if (targetPage) {
        const parsedPageId = targetPage?.pk ? entityKeyAsId(targetPage?.pk) : null;
        // page_drivers is a per-dataset union list; key each set by its dataset. A page may be
        // configured for any subset of datasets (e.g. HCTR-only) — never force GALAXY.
        const driverSetsByDataset = readPageDriverSets(targetPage.page_drivers);
        // Only seed a default when the page has no configured drivers at all (e.g. a fresh page).
        setPageId(parsedPageId);
        setPageDriverSets(
          isEmpty(driverSetsByDataset) ? { [DatasetSource.GALAXY]: DEFAULT_PAGE_DRIVERS } : driverSetsByDataset,
        );
        // Only update denominatorLabel from backend on Edit flow to avoid overwriting any User selection during Create
        if (currPageName) {
          setDenominatorLabel(targetPage.denominator_label ?? null);
          // Seed the Table Configuration selector + change-detection baseline from the page's
          // assigned layout family exactly once per edit session. Guarded so a pagesByName
          // refetch (from any SaveDeck's global query invalidation) does not clobber an unsaved
          // user selection back to the persisted value.
          if (!hasSeededLayoutFamily.current) {
            hasSeededLayoutFamily.current = true;
            setSelectedLayoutFamily(targetPage.layout_family ?? '');
            initialLayoutFamilyRef.current = targetPage.layout_family ?? '';
            setIsUpdateTemplate(true);
          }
        }
      } else {
        setPageId(null);
        setPageError(`Page with Name "${pageName}" not found. Unable to proceed.`);
      }
    }
  }, [isPageCreated, pageName, pagesByName, currPageName]);

  const contextValue = useMemo<PageSetupContextType>(
    () => ({
      pageSetupSteps,
      currentStep,
      nextStep,
      setCurrentStep,
      currDeckId: currentDeckId,
      currPageName,
      tocId,
      setTocId,
      tocEntry,
      isPageCreated,
      setIsPageCreated,
      pageName,
      setPageName,
      tocName,
      setTocName,
      pageHeaderName,
      setPageHeaderName,
      reportingPeriods,
      setReportingPeriods,
      denominatorLabel,
      setDenominatorLabel,
      selectedLayoutFamily,
      setSelectedLayoutFamily,
      assignLayoutFamily,
      isLayoutFamilySaving,
      hasTableConfigChanged,
      snapshotTableConfig,
      resetTableConfig,
      isUpdateTemplate,
      setIsUpdateTemplate,
      availableGrains,
      pageDriverSets,
      setPageDriverSets,
      enabledDatasets,
      decklineDatasetCounts,
      savePageDrivers,
      pageDriversSaving,
      pageError,
      pageLockStatus,
      isPageLockStatusResolved,
    }),
    [
      pageSetupSteps,
      currentStep,
      nextStep,
      currentDeckId,
      currPageName,
      isPageCreated,
      pageName,
      pageHeaderName,
      reportingPeriods,
      denominatorLabel,
      selectedLayoutFamily,
      assignLayoutFamily,
      isLayoutFamilySaving,
      hasTableConfigChanged,
      snapshotTableConfig,
      resetTableConfig,
      isUpdateTemplate,
      availableGrains,
      tocId,
      tocEntry,
      tocName,
      pageDriverSets,
      setPageDriverSets,
      enabledDatasets,
      decklineDatasetCounts,
      savePageDrivers,
      pageDriversSaving,
      pageError,
      pageLockStatus,
      isPageLockStatusResolved,
    ],
  );

  return <PageSetupContext value={contextValue}>{children}</PageSetupContext>;
};

/**
 * Custom hook to access Page Setup Context
 *
 * @returns {PageSetupContextType} The Page setup context value
 */
export const usePageSetupContext = (): PageSetupContextType => {
  return use(PageSetupContext);
};
